import { TimePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/TimePicker';
import {
  maxLengthRule,
  maxRule,
  minRule,
  requiredRule,
} from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import React, { useMemo } from 'react';
import {
  ToggleButton,
  ToggleButton as SingleToggleButton,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/ToggleButton';
import { CalendarConfigurationFormComponentProps } from '@lmt/unicorn-shared/app/components/calendar/configuration/common/types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { VisitType } from '../../visit/common/types';

import styles from './CalendarGeneralSettings.module.scss';
import { useEventCallback } from '@mui/material';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';

const unknownVisitTypeValue = 'visit_type.unknown';

export const CalendarGeneralSettings: React.FC<
  CalendarConfigurationFormComponentProps & {
    isSystem: boolean;
    isDuplicateName: (name: string) => boolean;
    isShowingTips: boolean;
    setShowingTips: (value: boolean) => void;
  }
> = (props) => {
  const i18n = useTranslation();
  const i18nCalendar = useScopedTranslation('calendar_configuration.general');
  const { register, control, setValue, formState } = props.formMethods;

  const visitTypes = useActiveVisitTypes();
  const translatedVisitTypes: VisitType[] = useMemo(
    () =>
      visitTypes
        .filter((vt) => vt.value !== unknownVisitTypeValue)
        .map((type) => ({
          id: type.id,
          label: i18n.t(type.value as any),
          specificStartTimeInSecondsSinceMidnight:
            type.specificStartTimeInSecondsSinceMidnight,
        })),
    [visitTypes],
  );

  const setVisitStartTime = useEventCallback((visitType: VisitType) => {
    if (visitType?.specificStartTimeInSecondsSinceMidnight) {
      setValue(
        'visitStartTimeInSeconds',
        visitType.specificStartTimeInSecondsSinceMidnight,
        validateAndDirty,
      );
    }
  });

  return (
    <>
      <div className={styles.configurationNameContainer}>
        <Field title={i18nCalendar.t('fields.name')} required={true}>
          <div className={styles.input}>
            <Input
              {...register('name', {
                setValueAs: (x: string) => x.trim(),
                ...requiredRule(),
                ...maxLengthRule(100),
              })}
              errorText={formState.errors.name?.message}
              disabled={props.isSystem}
            />
          </div>
        </Field>
        <ToggleButton
          title={i18n.t('calendar_configuration.blocks.tips')}
          checked={props.isShowingTips}
          onChange={(e) => props.setShowingTips(e.target.checked)}
        />
      </div>
      <Field title={''}>
        <SingleToggleButton
          {...register('showAbsences')}
          title={i18nCalendar.t('event_types.show_absences')}
        />
      </Field>
      <div className={styles.horizontalContainer}>
        <Field
          title={i18nCalendar.t('fields.default_visit_type')}
          required={true}
          className={styles.field}
        >
          <Controller
            control={control}
            name={'defaultVisitTypeId'}
            render={({ field: { onChange, value } }) => (
              <div className={styles.input}>
                <DropDownInput
                  useIdFunctionAsValue={true}
                  options={translatedVisitTypes}
                  idFunction={'id'}
                  value={value}
                  getOptionLabel={'label'}
                  required={true}
                  onValueChanged={(value) => {
                    onChange(value?.id ?? null);
                    setVisitStartTime(value);
                  }}
                  errorText={formState.errors.defaultVisitTypeId?.message}
                  variant={'formInput'}
                />
              </div>
            )}
            rules={requiredRule()}
          />
        </Field>
        <Field
          title={i18nCalendar.t('fields.visit_start_time')}
          required={true}
          className={styles.rightField}
        >
          <Controller
            control={control}
            name={'visitStartTimeInSeconds'}
            render={({ field: { onChange, value } }) => (
              <TimePicker
                timeInMills={value ? value * 1000 : value}
                onTimeChanged={(value) =>
                  onChange(value ? value / 1000 : value)
                }
                timeEntriesIntervalInMinutes={60}
                errorText={formState.errors.visitStartTimeInSeconds?.message}
              />
            )}
            rules={requiredRule()}
          />
        </Field>
      </div>
      <Field
        title={i18nCalendar.t('fields.time_slot_search_time_step')}
        required={true}
        className={styles.field}
      >
        <div className={styles.input}>
          <Input
            {...register('timeSlotSearchTimeStepInMinutes', {
              ...requiredRule(),
              ...minRule(1),
              ...maxRule(360), // 6 hours
            })}
            errorText={
              formState.errors.timeSlotSearchTimeStepInMinutes?.message
            }
            type={'number'}
          />
        </div>
      </Field>
      <Field title={''}>
        <SingleToggleButton
          {...register('displayWeekNumber')}
          title={i18nCalendar.t('fields.display_week_number')}
        />
      </Field>
    </>
  );
};
